/**
 * Núcleo del tracking de vídeo (v2). Contabilidad de una reproducción y
 * emisión de eventos; los adaptadores (html5/youtube/vimeo/custom) solo
 * alimentan este modelo.
 *
 * Tres relojes distintos, porque miden cosas distintas y confundirlos es la
 * causa habitual de métricas infladas:
 *
 *   watched_seconds  segundos de CONTENIDO consumidos (avance del media time;
 *                    un seek de 10 min no suma). Es "cuánto vídeo ha visto".
 *   elapsed_seconds  segundos de reloj con el player en play. En un directo
 *                    es la permanencia en la emisión.
 *   active_seconds   como elapsed pero solo mientras la pestaña está visible
 *                    y el player en pantalla. Es "cuánto ha estado ATENTO".
 *
 * En un VOD los tres se parecen. En un webinar de 2h con la gente cambiando
 * de pestaña, la diferencia entre elapsed y active es la métrica que importa.
 *
 * Para directos (live) no hay duración: los porcentajes no significan nada y
 * en su lugar mandan los hitos de tiempo absoluto (5', 15', 30'…) y el
 * heartbeat, que es lo que permite reconstruir la curva de concurrencia.
 */
import { uuidv4 } from '../uuid';

export type Props = Record<string, unknown>;

export type VideoConfig = {
  /** Segundos de reproducción entre heartbeats. 0 los desactiva. */
  heartbeatSeconds: number;
  /** Hitos de % de vídeo consumido (solo VOD). */
  percentMilestones: number[];
  /** Hitos de segundos vistos — los que mandan en directos y webinars. */
  timeMilestones: number[];
  /** % a partir del cual se considera visto completo. */
  completeAt: number;
  /** Registrar qué tramos del vídeo se han visto (curva de retención fina). */
  trackRanges: boolean;
  debug: boolean;
};

export const config: VideoConfig = {
  heartbeatSeconds: 15,
  percentMilestones: [10, 25, 50, 75, 90],
  timeMilestones: [30, 60, 180, 300, 600, 900, 1800, 2700, 3600, 5400, 7200],
  completeAt: 95,
  trackRanges: true,
  debug: false,
};

export function configure(partial: Partial<VideoConfig>): void {
  Object.assign(config, partial);
}

// ---------------------------------------------------------------------------
// Puente con el tracker principal
// ---------------------------------------------------------------------------

type Tracker = {
  track: (name: string, payload: Props) => unknown;
  flushNow?: () => void;
  flush?: (immediate?: boolean) => unknown;
};

export function getTracker(): Tracker | null {
  const w = window as unknown as { trackrift?: Tracker };
  return w.trackrift && typeof w.trackrift.track === 'function' ? w.trackrift : null;
}

/** Fuerza el envío inmediato (sendBeacon) — al abandonar la página. */
export function flushTracker(): void {
  const t = getTracker();
  if (!t) return;
  try {
    if (typeof t.flushNow === 'function') t.flushNow();
    else if (typeof t.flush === 'function') t.flush(true);
  } catch {
    /* noop */
  }
}

/** Observadores del debug overlay: reciben cada evento emitido. */
type Listener = (name: string, props: Props, session: VideoSession) => void;
const listeners: Listener[] = [];
export function onEvent(fn: Listener): void {
  listeners.push(fn);
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

const nowMs = (): number =>
  typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();

const round1 = (n: number): number => Math.round(n * 10) / 10;
const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/** [0,1,2,5,6,7] → "0-2,5-7". Tramos del vídeo realmente vistos, en centiles. */
export function compactRanges(buckets: Set<number>): string {
  const list = Array.from(buckets).sort((a, b) => a - b);
  const out: string[] = [];
  let start: number | null = null;
  let prev: number | null = null;
  for (const b of list) {
    if (start == null) { start = b; prev = b; continue; }
    if (prev != null && b === prev + 1) { prev = b; continue; }
    out.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = b; prev = b;
  }
  if (start != null) out.push(start === prev ? `${start}` : `${start}-${prev}`);
  return out.join(',');
}

// ---------------------------------------------------------------------------
// Sesión de reproducción
// ---------------------------------------------------------------------------

export type SessionInit = {
  id: string;
  title?: string | null;
  src?: string | null;
  duration?: number | null;
  player?: string;
  /** Emisión en directo: sin duración fiable, manda el tiempo de permanencia. */
  live?: boolean;
  webinarId?: string | null;
  /** Inicio programado del webinar (epoch ms) — para medir puntualidad. */
  startsAt?: number | null;
  /** Props fijas que viajan en todos los eventos de este vídeo. */
  props?: Props;
  /** Elemento asociado, para medir si está en pantalla. */
  el?: Element | null;
};

/** Lo que un adaptador reporta en cada pulso del reloj. */
export type Probe = { position: number; playing: boolean } | null;

export class VideoSession {
  readonly playId = uuidv4();
  readonly id: string;
  title: string | null;
  src: string | null;
  duration: number | null;
  player: string;
  live: boolean;
  webinarId: string | null;
  startsAt: number | null;
  props: Props;
  el: Element | null;

  seq = 0;
  position = 0;
  watched = 0;
  elapsed = 0;
  active = 0;
  audible = 0;
  maxPercent = 0;
  playing = false;
  muted = false;
  rate = 1;
  fullscreen = false;
  inView = true;
  quality: string | null = null;

  private impressionSent = false;
  private playSent = false;
  private completed = false;
  private exited = false;
  private readonly percentHits = new Set<number>();
  private readonly timeHits = new Set<number>();
  private readonly buckets = new Set<number>();
  private lastPos: number | null = null;
  private lastTickAt: number | null = null;
  private lastHeartbeatAt = 0;
  /** Adaptador: cómo preguntar el estado real al player en cada pulso. */
  probe: (() => Probe) | null = null;

  constructor(init: SessionInit) {
    this.id = init.id;
    this.title = init.title ?? null;
    this.src = init.src ?? null;
    this.duration = init.duration && init.duration > 0 ? init.duration : null;
    this.player = init.player ?? 'html5';
    this.live = init.live ?? false;
    this.webinarId = init.webinarId ?? null;
    this.startsAt = init.startsAt ?? null;
    this.props = init.props ?? {};
    this.el = init.el ?? null;
    register(this);
  }

  // -- emisión ---------------------------------------------------------------

  private baseProps(extra: Props = {}): Props {
    this.seq += 1;
    return {
      video_id: this.id,
      video_title: this.title ?? undefined,
      video_src: this.src ?? undefined,
      video_duration: this.duration != null ? round1(this.duration) : undefined,
      player: this.player,
      // play_id agrupa todos los eventos de UNA reproducción: sin él no se
      // puede reconstruir una sesión de visionado (ni la curva de un directo).
      play_id: this.playId,
      seq: this.seq,
      page_path: typeof location !== 'undefined' ? location.pathname : undefined,
      is_live: this.live || undefined,
      webinar_id: this.webinarId ?? undefined,
      position: round1(this.position),
      max_percent: this.duration ? Math.round(this.maxPercent) : undefined,
      seconds_watched: Math.round(this.watched),
      elapsed_seconds: Math.round(this.elapsed),
      active_seconds: Math.round(this.active),
      audible_seconds: Math.round(this.audible),
      muted: this.muted || undefined,
      playback_rate: this.rate !== 1 ? this.rate : undefined,
      quality: this.quality ?? undefined,
      ...this.props,
      ...extra,
    };
  }

  emit(name: string, extra: Props = {}): void {
    const props = this.baseProps(extra);
    for (const fn of listeners) {
      try { fn(name, props, this); } catch { /* el debug no puede romper el tracking */ }
    }
    const t = getTracker();
    if (!t) return;
    // event_id propio: el dedupe del tracker por (nombre + path) se comería
    // los cuartiles y heartbeats consecutivos.
    t.track(name, { event_id: uuidv4(), properties: props });
  }

  // -- ciclo de vida ---------------------------------------------------------

  impression(): void {
    if (this.impressionSent) return;
    this.impressionSent = true;
    this.emit('video_impression');
  }

  play(position = this.position): void {
    this.position = position;
    this.playing = true;
    this.lastTickAt = nowMs();
    this.lastPos = position;
    this.impression();
    // El punto de arranque cuenta como visto: sin esto un visionado desde el
    // principio empieza a registrar tramos en el segundo 1 y la curva de
    // retención arranca en el centil 1 en vez del 0.
    this.markBucket(position);
    if (!this.playSent) {
      this.playSent = true;
      const extra: Props = {};
      if (this.startsAt) {
        // Negativo = se conectó antes de la hora; positivo = llegó tarde.
        extra.join_offset_seconds = Math.round((Date.now() - this.startsAt) / 1000);
      }
      this.emit('video_play', extra);
    } else {
      this.emit('video_interaction', { action: 'resume' });
    }
  }

  pause(position = this.position): void {
    if (!this.playing) return;
    this.tick({ position, playing: true });
    this.playing = false;
    this.lastTickAt = null;
    this.lastPos = null;
    // `ended` dispara un pause en muchos players: no lo reportamos como
    // abandono, ya se emitió video_complete.
    if (!this.completed) this.emit('video_interaction', { action: 'pause' });
  }

  seek(to: number, from: number | null = this.lastPos): void {
    this.lastPos = null; // corta la acumulación: un salto no es visionado
    const delta = from != null ? to - from : null;
    this.position = to;
    this.emit('video_interaction', {
      action: 'seek',
      seek_from: from != null ? round1(from) : undefined,
      seek_to: round1(to),
      seek_delta: delta != null ? round1(delta) : undefined,
      direction: delta != null ? (delta < 0 ? 'backward' : 'forward') : undefined,
    });
  }

  setRate(rate: number): void {
    if (!rate || rate === this.rate) return;
    this.rate = rate;
    this.emit('video_interaction', { action: 'rate_change', playback_rate: rate });
  }

  setMuted(muted: boolean): void {
    if (muted === this.muted) return;
    this.muted = muted;
    this.emit('video_interaction', { action: muted ? 'mute' : 'unmute' });
  }

  setFullscreen(on: boolean): void {
    if (on === this.fullscreen) return;
    this.fullscreen = on;
    this.emit('video_interaction', { action: on ? 'fullscreen_enter' : 'fullscreen_exit' });
  }

  setQuality(q: string | null): void {
    if (!q || q === this.quality) return;
    this.quality = q;
  }

  setDuration(d: number | null | undefined): void {
    if (d != null && Number.isFinite(d) && d > 0) {
      this.duration = d;
      if (this.live && d > 0) this.live = this.live; // el directo mantiene flag
    }
  }

  error(code: string | number, message?: string): void {
    this.emit('video_interaction', { action: 'error', error_code: String(code), error_message: message });
  }

  /** CTA/oferta mostrada o pulsada durante el vídeo (webinars con pitch). */
  cta(action: 'shown' | 'click', label?: string): void {
    this.emit(action === 'click' ? 'video_cta_click' : 'video_cta_shown', { cta_label: label });
  }

  /** Gate de VSL superado (compat con la landing legacy de UDIA). */
  unlock(label?: string): void {
    this.emit('video_unlock', { cta_label: label });
  }

  complete(): void {
    if (this.completed) return;
    this.completed = true;
    // Un visionado completo pasó por todos los hitos aunque el player no los
    // reportara — sin esto el funnel sale no-monótono (75% = 0 con 100% > 0).
    for (const m of config.percentMilestones) {
      if (!this.percentHits.has(m)) {
        this.percentHits.add(m);
        this.emit('video_progress', { percent: m });
      }
    }
    this.emit('video_complete', { percent: 100 });
  }

  ended(): void {
    this.complete();
    this.playing = false;
    this.lastTickAt = null;
    this.lastPos = null;
  }

  /**
   * Cierre de la sesión: el resumen que hace innecesario reprocesar todos los
   * eventos intermedios. Se emite al abandonar la página o destruir el player.
   */
  exit(reason: string): void {
    if (this.exited || !this.impressionSent) return;
    this.exited = true;
    unregister(this);
    if (!this.playSent) return; // impresión sin play: nada que resumir
    this.emit('video_exit', {
      reason,
      coverage_percent: this.duration ? this.buckets.size : undefined,
      watched_ranges: config.trackRanges && this.buckets.size ? compactRanges(this.buckets) : undefined,
      completed: this.completed || undefined,
    });
  }

  // -- contabilidad ----------------------------------------------------------

  /** Marca el centil del vídeo correspondiente a `position` como visto. */
  private markBucket(position: number): void {
    if (!config.trackRanges || this.live || !this.duration || this.duration <= 0) return;
    this.buckets.add(clamp(Math.floor((position / this.duration) * 100), 0, 99));
  }

  private attentive(): boolean {
    const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
    return visible && this.inView;
  }

  /**
   * Pulso del reloj (1/s) o evento del player. `probe` puede venir null si el
   * adaptador no sabe el estado — entonces solo se cierra el intervalo.
   */
  tick(p: Probe): void {
    if (this.exited) return;
    if (!p) { this.lastTickAt = null; this.lastPos = null; return; }
    const { position, playing } = p;
    const wallNow = nowMs();
    const wallDt = this.lastTickAt == null ? 0 : (wallNow - this.lastTickAt) / 1000;
    this.lastTickAt = wallNow;

    if (!playing) {
      this.playing = false;
      this.lastPos = null;
      this.position = position;
      return;
    }
    this.playing = true;

    // Un salto de reloj enorme = pestaña dormida, máquina suspendida o
    // setInterval estrangulado en segundo plano. Ahí no sabemos qué pasó, así
    // que el intervalo se DESCARTA ENTERO — para los tres relojes.
    //
    // Descartarlo solo en elapsed y no en watched fue un bug real: la
    // tolerancia del delta de contenido escala con wallDt, así que un hueco de
    // 60s se colaba íntegro como "visionado" y salían sesiones con más
    // contenido visto que tiempo de reproducción.
    const gap = !(wallDt > 0 && wallDt < 10);

    if (!gap) {
      this.elapsed += wallDt;
      if (this.attentive()) this.active += wallDt;
      if (!this.muted) this.audible += wallDt;

      if (this.lastPos != null) {
        const mediaDt = position - this.lastPos;
        // Tolerancia = lo que cabe en el intervalo a la velocidad actual.
        // Filtra los seeks hacia delante sin descartar reproducción a 2x.
        const maxDt = Math.max(2, wallDt * (this.rate + 0.5) + 1);
        if (mediaDt > 0 && mediaDt <= maxDt) this.watched += mediaDt;
      }
    }
    this.lastPos = position;
    this.position = position;

    if (this.duration && this.duration > 0 && !this.live) {
      const pct = clamp((position / this.duration) * 100, 0, 100);
      if (pct > this.maxPercent) this.maxPercent = pct;
      this.markBucket(position);
      for (const m of config.percentMilestones) {
        if (pct >= m && !this.percentHits.has(m)) {
          this.percentHits.add(m);
          this.emit('video_progress', { percent: m });
        }
      }
      if (pct >= config.completeAt) this.complete();
    }

    // Hitos de tiempo: en VOD sobre contenido consumido; en directo sobre
    // permanencia real (lo único que existe cuando no hay duración).
    const timeBase = this.live ? this.elapsed : this.watched;
    for (const m of config.timeMilestones) {
      if (timeBase >= m && !this.timeHits.has(m)) {
        this.timeHits.add(m);
        this.emit('video_milestone', { threshold_seconds: m });
      }
    }

    if (config.heartbeatSeconds > 0 && this.elapsed - this.lastHeartbeatAt >= config.heartbeatSeconds) {
      this.lastHeartbeatAt = this.elapsed;
      this.emit('video_heartbeat', {
        percent: this.duration && !this.live ? Math.round(this.maxPercent) : undefined,
        attentive: this.attentive(),
      });
    }
  }

  /** Checkpoint sin esperar al heartbeat (al ocultar la pestaña). */
  checkpoint(reason: string): void {
    if (this.exited || !this.playSent) return;
    this.lastHeartbeatAt = this.elapsed;
    this.emit('video_heartbeat', { reason, attentive: this.attentive() });
  }

  snapshot(): Props {
    return {
      id: this.id,
      player: this.player,
      live: this.live,
      playing: this.playing,
      position: round1(this.position),
      duration: this.duration,
      percent: this.duration ? Math.round(this.maxPercent) : null,
      watched: Math.round(this.watched),
      elapsed: Math.round(this.elapsed),
      active: Math.round(this.active),
      inView: this.inView,
      muted: this.muted,
    };
  }
}

// ---------------------------------------------------------------------------
// Registro global + reloj único
// ---------------------------------------------------------------------------

const sessions: VideoSession[] = [];
let clock: ReturnType<typeof setInterval> | null = null;

export function activeSessions(): VideoSession[] {
  return sessions.slice();
}

function register(s: VideoSession): void {
  sessions.push(s);
  startClock();
}

function unregister(s: VideoSession): void {
  const i = sessions.indexOf(s);
  if (i >= 0) sessions.splice(i, 1);
}

/**
 * Un solo intervalo para todas las sesiones. Los adaptadores exponen `probe()`
 * y aquí se les pregunta: así YouTube (que no emite timeupdate) y <video>
 * comparten exactamente la misma contabilidad.
 */
function startClock(): void {
  if (clock || typeof window === 'undefined') return;
  clock = setInterval(() => {
    for (const s of sessions) {
      if (s.probe) s.tick(s.probe());
    }
  }, 1000);
}

/** Observa si el player está realmente en pantalla (afecta a active_seconds). */
export function observeVisibility(s: VideoSession): void {
  if (!s.el || typeof IntersectionObserver === 'undefined') return;
  try {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) s.inView = e.isIntersecting && e.intersectionRatio >= 0.3;
      },
      { threshold: [0, 0.3, 0.6] },
    );
    io.observe(s.el);
  } catch {
    /* noop */
  }
}

let lifecycleBound = false;

/** Checkpoints al ocultar y resumen al abandonar. Idempotente. */
export function bindLifecycle(): void {
  if (lifecycleBound || typeof window === 'undefined') return;
  lifecycleBound = true;

  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState !== 'hidden') return;
      for (const s of sessions) if (s.playing) s.checkpoint('hidden');
      flushTracker();
    },
    { passive: true },
  );

  const onLeave = () => {
    for (const s of sessions.slice()) s.exit('pagehide');
    flushTracker();
  };
  window.addEventListener('pagehide', onLeave, { passive: true });
  // Safari iOS no siempre dispara pagehide en navegación por gestos.
  window.addEventListener('beforeunload', onLeave, { passive: true });
}
